import {ranges, walk} from "./util.js";
import {getCustomProp} from "./css.js";
import {type Point} from "./util/ranges.js";
import {counterChangeScopesSym, counterScopesCarrierSym, counterScopesSym} from "./counters.js";

export type Position = "before" | "after";
export type Unbreakable =
	'float'
	| 'intrisicDimension'
	| 'horizontalLayout'
	| 'replacedElement'
	| 'subFragmentor'
	| false;

const REPLACED_ELEMENTS = ['img', 'object', 'video', 'audio', 'canvas', 'iframe', 'textarea', 'input', 'button'];

export interface Breaker<E extends HTMLElement = HTMLElement> {
	matches: (elem: HTMLElement) => elem is E;
	break: (before: E, after: E) => void;
	unbreak: (before: E, after: E) => void;
}

export const breakers: Breaker[] = [];

export const breakedAfterSym = Symbol("ps-breaked-after");
export const breakedBeforeSym = Symbol("ps-breaked-before");

export interface BreakedElement extends HTMLElement {
	[breakedAfterSym]?: BreakedElement;
	[breakedBeforeSym]?: BreakedElement;
	parentElement: BreakedElement | null;
}

export function breakAtPoint<T extends HTMLElement>(source: T, breakPoint: Point, side: Position = 'after'): T {
	let textBreak = breakPoint.container.nodeType == Node.TEXT_NODE;
	const breaking = textBreak ? breakPoint.container.parentElement : breakPoint.container as HTMLElement;
	const nodeAtBreak = ranges.nodeAtPoint(breakPoint);
	const inlineBreak = nodeAtBreak && (nodeAtBreak.nodeType == Node.TEXT_NODE || getComputedStyle(nodeAtBreak as Element).display == "inline");
	const breakingOnSource = breaking == source;

	// An attribute is set to find the breaking element after the extraction of contents
	breaking.setAttribute('ps-breaking', '');

	const contentsRange = source.ownerDocument.createRange();
	if (side == "before") {
		contentsRange.setStartBefore(source);
		contentsRange.setEnd(breakPoint.container, breakPoint.offset);
	} else {
		contentsRange.setStart(breakPoint.container, breakPoint.offset);
		contentsRange.setEndAfter(source);
	}
	const contents = contentsRange.extractContents();
	const dest = contents.firstElementChild as T;

	let sourceBreaking = breakingOnSource ? source : source.querySelector('[' + 'ps-breaking' + ']') as BreakedElement;
	let destBreaking = breakingOnSource ? dest : dest.querySelector('[' + 'ps-breaking' + ']') as BreakedElement;

	sourceBreaking.removeAttribute('ps-breaking');
	destBreaking.removeAttribute('ps-breaking');

	let firstAncestorBox = false;
	while (destBreaking != dest.parentElement) {
		let beforeElt: BreakedElement, afterElt: BreakedElement;
		if (side == "after") {
			beforeElt = sourceBreaking;
			afterElt = destBreaking;
			// Counters are copied from the preceding breaked element to the following one.
			if (counterScopesSym in beforeElt) afterElt[counterScopesCarrierSym] = beforeElt;
			else if (counterScopesCarrierSym in  beforeElt) afterElt[counterScopesCarrierSym] = beforeElt[counterScopesCarrierSym];
			if (counterChangeScopesSym in beforeElt) afterElt[counterChangeScopesSym] = beforeElt[counterChangeScopesSym];
		} else {
			beforeElt = destBreaking;
			afterElt = sourceBreaking;
			// Counter changes are modified to point to the preceding breaked element.
			if (counterScopesSym in afterElt) {
				beforeElt[counterScopesSym] = afterElt[counterScopesSym];
				delete afterElt[counterScopesSym];
				afterElt[counterScopesCarrierSym] = beforeElt;
			} else if (counterScopesCarrierSym in afterElt) {
				beforeElt[counterScopesCarrierSym] = afterElt[counterScopesCarrierSym];
			}
			// Counter changes are modified to point to the preceding breaked element.
			if (counterChangeScopesSym in afterElt) {
				for( const counterName in afterElt[counterChangeScopesSym]) {
					const scope = afterElt[counterChangeScopesSym][counterName];
					for (const change of scope.changes) {
						if (change.element == afterElt) change.element = beforeElt;
					}
					beforeElt[counterChangeScopesSym] = afterElt[counterChangeScopesSym];
				}
			}
		}

		beforeElt.setAttribute('ps-breaked-after', '');
		afterElt.setAttribute('ps-breaked-before', '');
		if (beforeElt[breakedAfterSym]) {
			afterElt[breakedAfterSym] = beforeElt[breakedAfterSym];
			beforeElt[breakedAfterSym][breakedBeforeSym] = afterElt;
		}
		beforeElt[breakedAfterSym] = afterElt;
		if (afterElt[breakedBeforeSym]) {
			beforeElt[breakedBeforeSym] = afterElt[breakedBeforeSym];
			afterElt[breakedBeforeSym][breakedAfterSym] = beforeElt;
		}
		afterElt[breakedBeforeSym] = beforeElt;

		for (const breaker of breakers) {
			if (breaker.matches(destBreaking)) {
				breaker.break(beforeElt, afterElt);
				break;
			}
		}

		if (textBreak) {
			const textNode = breakPoint.container;
			/* Force an hyphen if the break is on one */
			// TODO check that the 'hyphens' property != none
			if (textNode.nodeValue.endsWith('\u00AD')) {
				const style = getComputedStyle(textNode.parentElement);
				if (style.getPropertyValue('--ps-hyphens').trim() == "auto") {
					const hyphenChar = style.getPropertyValue("-webkit-hyphenate-character")[1] || '\u2010';
					textNode.nodeValue = textNode.nodeValue.slice(0, -1) + hyphenChar;
					textNode.parentElement.setAttribute('ps-hyphen-last', '');
				}
			}
			textBreak = false;
		}
		if (inlineBreak && !firstAncestorBox && walk.displayAsBlock(beforeElt)) {
			beforeElt.style.textAlignLast = beforeElt.style.MozTextAlignLast = getComputedStyle(beforeElt).textAlign;
			firstAncestorBox = true;
		}

		sourceBreaking = sourceBreaking.parentElement as BreakedElement;
		destBreaking = destBreaking.parentElement as BreakedElement;
	}

	return dest;
}

// TODO removeBreakedAttr: keep track of the breaked element before / after in a symbol instead?
export function unbreak(before: BreakedElement, after: BreakedElement, position: Position = 'before', scopeCarrierStack: Element[] = []): void {
	if (position === 'before') {
		if (walk.isBreakedAfter(after)) before[breakedAfterSym] = after[breakedAfterSym];
		else {
			before.removeAttribute('ps-breaked-after');
			delete before[breakedAfterSym];
		}
	} else {
		if (walk.isBreakedBefore(before)) after[breakedBeforeSym] = before[breakedBeforeSym];
		else {
			after.removeAttribute('ps-breaked-before');
			delete after[breakedBeforeSym];
		}
	}

	for (const breaker of breakers) {
		if (breaker.matches(before)) {
			breaker.unbreak(before, after);
			break;
		}
	}

	if (before.style.textAlignLast) {
		before.style.textAlignLast = before.style.MozTextAlignLast = '';
		if (!before.getAttribute('style')) before.removeAttribute('style');
	}

	if (after.hasAttribute('ps-counters')) {
		after.style.counterSet = '';
		after.removeAttribute('ps-counters');
	}

	if (after.hasAttribute('ps-counters-on-desc')) {
		after.style.removeProperty('--ps-counters-on-desc');
		after.removeAttribute('ps-counters-on-desc');
	}

	let childBefore: HTMLElement, childAfter: HTMLElement;
	if (walk.isHTMLElement(before.lastChild) && walk.isBreakedAfter(before.lastChild)) {
		childBefore = before.lastChild;
		childAfter = after.firstChild as HTMLElement;
	}

	// Maybe a text break
	if (!childBefore) {
		if (before.hasAttribute('ps-hyphen-last')) {
			before.lastChild.nodeValue = before.lastChild.nodeValue.slice(0, -1) + '\u00AD';
			before.removeAttribute('ps-hyphen-last');
		}
	}

	if (position == 'before') {
		while (after.firstChild) before.appendChild(after.firstChild);
		after.remove();
		before.normalize();
	} else {
		// Copy of the attributes of the before when the unbreak lands on the after
		if (before.hasAttribute('start')) after.setAttribute('start', before.getAttribute('start'));
		while (before.lastChild) after.insertBefore(before.lastChild, after.firstChild);
		before.remove();
		after.normalize();
	}

	if (counterScopesSym in before) scopeCarrierStack.push(before);
	if (childBefore && childAfter) unbreak(childBefore, childAfter, position, scopeCarrierStack);
}

const orderedListBreaker: Breaker<HTMLOListElement> = {
	matches(elem): elem is HTMLOListElement {
		return elem.localName == "ol";
	},
	break(before, after): void {
		const listItems = Array.prototype.filter.call(before.children, (child: Element) => {
			return getComputedStyle(child).display == 'list-item';
		});
		let beforeStart = before.hasAttribute('start') ? parseInt(before.getAttribute('start')) : 1;
		// Substract one if the first child is breaked
		if (after.firstElementChild && walk.isBreakedBefore(after.firstElementChild)) beforeStart--;
		after.setAttribute('start', beforeStart + listItems.length);
	},
	unbreak(before, after) {
		after.removeAttribute('start');
	}
};

const tableBreaker: Breaker<HTMLTableElement> = {
	matches: walk.isHTMLTable,
	break(before, after): void {
		const colgroup = before.getElementsByTagName('colgroup').item(0);
		if (colgroup) {
			const afterCol = after.insertBefore(colgroup.cloneNode(true), after.firstChild);
			afterCol.setAttribute('ps-repeated', '');
		}

		const style = getComputedStyle(before);
		if (!after.tHead && after.tBodies.length && before.tHead && getCustomProp(style, 'repeat-thead') != 'no-repeat') {
			after.tHead = before.tHead.cloneNode(true);
			after.tHead.setAttribute('ps-repeated', '');
		}
		/* It is not possible to repeat a footer or the bottom caption:  the elements must be known before a page break can occur. */
	},
	unbreak(before, after) {
		const afterColgroups = after.querySelectorAll('colgroup');
		for (let i = 1; i < afterColgroups.length; i++) afterColgroups[i].remove();
		if (after.tHead && after.tHead.hasAttribute('ps-repeated')) after.tHead.remove();
	}
};

breakers.push(orderedListBreaker);
breakers.push(tableBreaker);

// Only the intrinsic dimension specified in the element's style is taken into account.
export function unbreakableElement(element: Element): Unbreakable {
	if (!walk.isHTMLElement(element)) return 'replacedElement';
	const style = getComputedStyle(element);
	if (style.float != 'none') return 'float';
	if (element.style.height || element.style.minHeight) return 'intrisicDimension';
	if (REPLACED_ELEMENTS.includes(element.localName) && walk.displayAsBlock(element)) return 'replacedElement';
}
